import { Box, IconButton, Paper, Popover, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row } from 'react-table';
import ClearIcon from '../../../assets/icons/clear-icon';
import FilterIcon from '../../../assets/icons/filter-icon';
import { useMountEffect } from '../../../globals/hooks/use-mount-effect';
import theme from '../../../globals/theme';
import { AppState } from '../../../store';
import { savePreFlashFilter } from '../../../store/bulk-device-flash/actions';
import useStyles from './styles';

type FilterProperties = {
  column: {
    filterValue: string[];
    setFilter: Function;
    preFilteredRows: Row[];
    id: string;
  };
};

export const SelectColumnFilter = (filterProperties: FilterProperties) => {
  const {
    column: { setFilter, preFilteredRows, id, filterValue },
  } = filterProperties;
  const dispatch = useDispatch();
  const checkFilter = useSelector((state: AppState) => state.bulkFlash.preFlashFilters)[id] || [];
  const [inputValue, setInputValue] = useState('');
  const [anchorElement, setAnchorElement] = React.useState<HTMLButtonElement | null>(null);
  const classes = useStyles();

  const updateFilter = (filterList: string[]) => {
    if (filterList.length > 0) setFilter(filterList);
    else setFilter(undefined);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleInputChange = (event: any) => {
    setInputValue(event.target.value || '');
  };

  const constructArrayToSet = (existsIndex: number, option: string) => {
    if (existsIndex > -1) {
      const checkFilterCopy = [...checkFilter];
      checkFilterCopy.splice(existsIndex, 1);
      return checkFilterCopy;
    }
    return [...checkFilter, option];
  };

  const handleCheckFilter = (existsIndex: number, option: string) => {
    const filterList = constructArrayToSet(existsIndex, option);
    updateFilter(filterList);
    dispatch(
      savePreFlashFilter({
        [id]: filterList,
      }),
    );
  };

  const open = Boolean(anchorElement);

  const activeFilterColor =
    checkFilter.length > 0 ? theme.palette.info.dark : theme.palette.grey[400];

  const count = preFilteredRows.length;

  const options = useMemo(() => {
    const optionSet = new Set();
    preFilteredRows.forEach((row: any) => {
      optionSet.add(row.values[id]);
    });
    return [...optionSet.values()];
  }, [id, preFilteredRows]);

  useMountEffect(() => {
    updateFilter(checkFilter);
  });
  const resetFilters = useCallback(() => {
    if (filterValue && filterValue !== checkFilter) setFilter(undefined);
  }, [checkFilter, filterValue, setFilter]);

  useEffect(() => {
    resetFilters();
  }, [checkFilter, resetFilters]);

  return <>
    <IconButton className={classes.iconButton} onClick={handleClick} size="large">
      <FilterIcon width="13px" height="13px" stroke={activeFilterColor} />
    </IconButton>
    <Popover
      id={id}
      open={open}
      anchorEl={anchorElement}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Paper>
        <Box p={1} pb={0} display="flex" justifyContent="space-between">
          <Typography>Filter {count} records...</Typography>
          <Tooltip title="Clear Selection" enterDelay={700}>
            <span>
              <IconButton
                disabled={!count}
                onClick={() => {
                  setFilter(undefined);
                  dispatch(savePreFlashFilter({ [id]: [] }));
                }}
                className={classes.cancelButton}
                size="large">
                <ClearIcon
                  stroke={`${!count ? theme.palette.grey[400] : theme.palette.grey[600]}`}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box p={1} pb={0}>
          <input
            value={inputValue || ''}
            onChange={handleInputChange}
            placeholder="Narrow Filter Options"
          />
        </Box>
        <Box p={1}>
          <div className={classes.selectContainer}>
            {options
              .filter((option: any) =>
                inputValue !== '' && inputValue !== null ? option.includes(inputValue) : true,
              )
              .map((option: any) => {
                const existsIndex = checkFilter.indexOf(option);
                return (
                  <div key={option}>
                    <input
                      type="checkbox"
                      onChange={() => handleCheckFilter(existsIndex, option)}
                      checked={existsIndex > -1}
                    />
                    {option}
                  </div>
                );
              })}
          </div>
        </Box>
      </Paper>
    </Popover>
  </>;
};
